/**
 * useSiteContent — fetches admin-editable content overrides for a page.
 *
 * Usage:
 *   const { sections, loaded } = useSiteContent('homepage');
 *   const title = sections.hero_title || 'Default title';
 *
 * Hardcoded defaults live in the page itself; this hook only returns admin overrides.
 * `loaded=false` until the fetch completes — page should render defaults during this state.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const cache = new Map();

/** Imperatively invalidate the cache for a given pageId (e.g. after admin save). */
export const invalidateSiteContent = (pageId) => {
  cache.delete(pageId);
  window.dispatchEvent(new CustomEvent('site-content-invalidate', { detail: { pageId } }));
};

export const useSiteContent = (pageId) => {
  const [sections, setSections] = useState(() => cache.get(pageId) || {});
  const [loaded, setLoaded] = useState(() => cache.has(pageId));

  useEffect(() => {
    let cancelled = false;
    const fetchIt = async () => {
      try {
        const { data } = await axios.get(`${API}/site-content/${pageId}`);
        const s = data?.sections || {};
        cache.set(pageId, s);
        if (!cancelled) {
          setSections(s);
          setLoaded(true);
        }
      } catch (err) {
        console.error(`[useSiteContent] fetch failed for ${pageId}:`, err);
        if (!cancelled) setLoaded(true);
      }
    };
    fetchIt();
    const handler = (e) => {
      if (!e?.detail?.pageId || e.detail.pageId === pageId) fetchIt();
    };
    window.addEventListener('site-content-invalidate', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('site-content-invalidate', handler);
    };
  }, [pageId]);

  return { sections, loaded };
};

/** Pick a key with fallback. Supports rich text HTML when isHtml=true. */
export const pick = (sections, key, fallback) => {
  const v = sections?.[key];
  if (v === undefined || v === null || v === '') return fallback;
  return v;
};
